import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { NotificationManager } from 'react-notifications';
import {
  isOrgAdmin,
  memberDisplayName,
  primaryRoleLabel,
} from '@/lib/account-roles';
import {
  useAddOrgMemberMutation,
  useDeleteOrgMemberMutation,
  useOrgMembersQuery,
  useUpdateAccountMutation,
  useUpdateOrgMemberMutation,
  useUpdateOrgMutation,
} from '@/queries/account';
import { useAuthStore } from '@/stores/auth-store';
import { Loading } from '../controls';
import { ChangePasswordForm } from '../password';
import Portal from '../modal';
import TeamMemberModal from './TeamMemberModal';

const EMAIL_RE = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+$/;

function optionalUrl(value) {
  const v = value?.trim();
  if (!v) {
    return undefined;
  }
  try {
    new URL(v.includes('://') ? v : `https://${v}`);
    return undefined;
  } catch {
    return 'Enter a valid URL';
  }
}

function buildInitialValues(user) {
  const org = user?.organization ?? {};
  return {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    accountName: user?.accountName ?? '',
    phoneNumber: user?.phoneNumber ?? '',
    orgName: org?.orgName ?? '',
    emailAddress: org?.emailAddress ?? '',
    websiteAddress: org?.websiteAddress ?? '',
  };
}

export default function Account() {
  const currentUser = useAuthStore((s) => s.currentUser);
  const canManageOrg = isOrgAdmin(currentUser);

  const {
    data: members = [],
    isPending: membersLoading,
    isError: membersError,
  } = useOrgMembersQuery();

  const updateAccount = useUpdateAccountMutation();
  const updateOrg = useUpdateOrgMutation();
  const addMember = useAddOrgMemberMutation();
  const updateMember = useUpdateOrgMemberMutation();
  const deleteMember = useDeleteOrgMemberMutation();

  const [teamModal, setTeamModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const initialValues = useMemo(
    () => buildInitialValues(currentUser),
    [currentUser],
  );

  const validate = (values) => {
    const errors = {};
    if (!values.firstName?.trim()) {
      errors.firstName = 'Required';
    }
    if (!values.lastName?.trim()) {
      errors.lastName = 'Required';
    }
    if (!values.accountName?.trim()) {
      errors.accountName = 'Required';
    } else if (!EMAIL_RE.test(values.accountName.trim())) {
      errors.accountName = 'Enter a valid email address';
    }
    if (canManageOrg) {
      if (!values.orgName?.trim()) {
        errors.orgName = 'Required';
      }
      const urlErr = optionalUrl(values.websiteAddress);
      if (urlErr) {
        errors.websiteAddress = urlErr;
      }
    }
    return errors;
  };

  const handleSave = async (values, { resetForm }) => {
    try {
      await updateAccount.mutateAsync({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        accountName: values.accountName.trim(),
        phoneNumber: values.phoneNumber?.trim() || null,
      });

      if (canManageOrg) {
        const org = currentUser?.organization ?? {};
        await updateOrg.mutateAsync({
          orgId: org.orgId,
          orgName: values.orgName.trim(),
          emailAddress: values.emailAddress?.trim() || null,
          websiteAddress: values.websiteAddress?.trim() || null,
          orgPhoto: org.orgPhoto ?? null,
        });
      }

      NotificationManager.success('Account settings saved', '', 3000);
      resetForm({ values });
    } catch {
      /* errors surfaced by mutation hooks */
    }
  };

  const handleTeamSubmit = async (values) => {
    try {
      if (teamModal?.mode === 'add') {
        await addMember.mutateAsync(values);
      } else {
        await updateMember.mutateAsync(values);
      }
      setTeamModal(null);
    } catch {
      /* notification from mutation */
    }
  };

  const handleDeleteMember = async () => {
    if (!deleteTarget?.accountName) {
      return;
    }
    try {
      await deleteMember.mutateAsync(deleteTarget.accountName);
      setDeleteTarget(null);
    } catch {
      /* notification from mutation */
    }
  };

  const saving = updateAccount.isPending || updateOrg.isPending;

  const teamBusy =
    addMember.isPending || updateMember.isPending || deleteMember.isPending;

  return (
    <div className="w-full">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validate={validate}
        onSubmit={handleSave}
      >
        {({ resetForm }) => (
          <Form
            id="accountForm"
            className="admin-form bethere-content-container"
          >
            <div className="event-main-info flex min-w-0 flex-col gap-6">
              <div className="glass-panel glass-panel-interactive p-6 sm:p-8">
                <h1 className="mb-6 text-left text-[clamp(1.35rem,2vw+0.8rem,1.85rem)] font-bold text-[#121212]">
                  Account Settings
                </h1>
                <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-[#121212]">
                  <FontAwesomeIcon
                    icon={['fas', 'user']}
                    className="text-[var(--bethere-primary)]"
                  />
                  Personal Information
                </h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="input-group">
                    <label htmlFor="acct-first">First Name</label>
                    <Field
                      id="acct-first"
                      name="firstName"
                      type="text"
                      placeholder="First Name"
                    />
                    <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                      <ErrorMessage name="firstName" />
                    </div>
                  </div>
                  <div className="input-group">
                    <label htmlFor="acct-last">Last Name</label>
                    <Field
                      id="acct-last"
                      name="lastName"
                      type="text"
                      placeholder="Last Name"
                    />
                    <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                      <ErrorMessage name="lastName" />
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="input-group">
                    <label htmlFor="acct-email">Email Address</label>
                    <Field
                      id="acct-email"
                      name="accountName"
                      type="email"
                      placeholder="Email"
                    />
                    <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                      <ErrorMessage name="accountName" />
                    </div>
                  </div>
                  <div className="input-group">
                    <label htmlFor="acct-phone">Phone Number</label>
                    <Field
                      id="acct-phone"
                      name="phoneNumber"
                      type="tel"
                      placeholder="Phone"
                    />
                  </div>
                </div>
              </div>

              <div className="glass-panel glass-panel-interactive p-6 sm:p-8">
                <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-[#121212]">
                  <FontAwesomeIcon
                    icon={['fas', 'building']}
                    className="text-[var(--bethere-primary)]"
                  />
                  Organization Details
                </h2>
                {!canManageOrg && (
                  <p className="mb-4 text-sm text-[var(--festy-muted-text)]">
                    Organization details can only be changed by an admin.
                  </p>
                )}
                <div className="input-group">
                  <label htmlFor="acct-org-name">Organization Name</label>
                  <Field
                    id="acct-org-name"
                    name="orgName"
                    type="text"
                    placeholder="Org Name"
                    disabled={!canManageOrg}
                  />
                  <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                    <ErrorMessage name="orgName" />
                  </div>
                </div>
                <div className="input-group">
                  <label htmlFor="acct-org-email">
                    Organization Email (Public)
                  </label>
                  <Field
                    id="acct-org-email"
                    name="emailAddress"
                    type="email"
                    placeholder="Public Contact Email"
                    disabled={!canManageOrg}
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="acct-org-web">Organization Website</label>
                  <Field
                    id="acct-org-web"
                    name="websiteAddress"
                    type="url"
                    placeholder="https://example.com"
                    disabled={!canManageOrg}
                  />
                  <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                    <ErrorMessage name="websiteAddress" />
                  </div>
                </div>
              </div>
            </div>

            <aside className="bethere-detail-sidebar">
              <div className="glass-panel p-6">
                <h3 className="mb-2 text-lg font-bold text-[#121212]">
                  Save Changes
                </h3>
                <p className="mb-4 text-sm text-[var(--festy-muted-text)]">
                  Update your profile and organization details to keep your
                  event listings accurate.
                </p>
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="accountForm"
                    disabled={saving}
                    className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] bg-[var(--bethere-accent)] px-6 font-bold text-[#121212] shadow-sm transition hover:bg-[var(--bethere-secondary)] disabled:opacity-60"
                  >
                    <FontAwesomeIcon icon={['fas', 'floppy-disk']} />
                    Save
                  </button>
                </div>
              </div>

              <div className="glass-panel p-6">
                <h3 className="mb-4 text-lg font-bold text-[#121212]">
                  Team Members
                </h3>
                {membersLoading && <Loading />}
                {membersError && (
                  <p className="text-sm text-red-600">
                    Could not load team members.
                  </p>
                )}
                {!membersLoading && !membersError && (
                  <>
                    <div className="manage-table-container -mx-2 overflow-x-auto px-2">
                      <table className="manage-table w-full min-w-[280px]">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Role</th>
                            {canManageOrg && <th>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((member) => {
                            const admin = isOrgAdmin(member);
                            return (
                              <tr key={member.accountName}>
                                <td>
                                  {admin ? (
                                    <strong>{memberDisplayName(member)}</strong>
                                  ) : (
                                    memberDisplayName(member)
                                  )}
                                </td>
                                <td className="text-sm">
                                  {primaryRoleLabel(member)}
                                </td>
                                {canManageOrg && (
                                  <td>
                                    <div className="manage-actions">
                                      {!admin && (
                                        <>
                                          <button
                                            type="button"
                                            className="bethere-action-btn"
                                            aria-label="Edit member"
                                            title="Edit member"
                                            onClick={() =>
                                              setTeamModal({
                                                mode: 'edit',
                                                member,
                                              })
                                            }
                                          >
                                            <FontAwesomeIcon
                                              icon={['fas', 'pen-to-square']}
                                            />
                                          </button>
                                          <button
                                            type="button"
                                            className="bethere-action-btn bg-[var(--bethere-primary)] text-white hover:brightness-110"
                                            aria-label="Remove member"
                                            title="Remove member"
                                            onClick={() =>
                                              setDeleteTarget(member)
                                            }
                                          >
                                            <FontAwesomeIcon
                                              icon={['fas', 'trash']}
                                            />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {canManageOrg && (
                      <button
                        type="button"
                        className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] border-2 border-dashed border-[var(--bethere-primary)] bg-transparent font-bold text-[var(--bethere-primary)] transition hover:bg-[var(--bethere-primary)]/5"
                        onClick={() => setTeamModal({ mode: 'add' })}
                      >
                        <FontAwesomeIcon icon={['fas', 'plus']} />
                        Add Member
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="glass-panel p-6">
                <h3 className="mb-4 text-lg font-bold text-[#121212]">
                  Account Security
                </h3>
                <ul className="meta-list mb-4 list-none p-0">
                  <li>
                    <FontAwesomeIcon icon={['fas', 'shield-halved']} />
                    <div>
                      <strong>Password</strong>
                      <span className="text-sm text-[var(--festy-muted-text)]">
                        Update your password to keep your account secure.
                      </span>
                    </div>
                  </li>
                </ul>
                <button
                  type="button"
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] border-2 border-[var(--bethere-primary)] bg-transparent font-bold text-[var(--bethere-primary)] transition hover:bg-[var(--bethere-primary)]/5"
                  onClick={() => setPasswordModalOpen(true)}
                >
                  <FontAwesomeIcon icon={['fas', 'key']} />
                  Change Password
                </button>
              </div>
            </aside>
          </Form>
        )}
      </Formik>

      <Portal
        header={
          teamModal?.mode === 'edit' ? 'Edit Team Member' : 'Add Team Member'
        }
        open={Boolean(teamModal)}
        onClose={() => setTeamModal(null)}
      >
        {teamModal && (
          <TeamMemberModal
            mode={teamModal.mode}
            member={teamModal.member}
            isSubmitting={teamBusy}
            onCancel={() => setTeamModal(null)}
            onSubmit={handleTeamSubmit}
          />
        )}
      </Portal>

      <Portal
        header="Change Password"
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      >
        <ChangePasswordForm
          onCancel={() => setPasswordModalOpen(false)}
          onSuccess={() => setPasswordModalOpen(false)}
        />
      </Portal>

      <Portal
        header="Remove Team Member"
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        <p className="mb-6 text-sm text-[#121212]">
          Remove <strong>{memberDisplayName(deleteTarget)}</strong> from your
          organization? They will lose access immediately.
        </p>
        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            className="inline-flex min-h-11 items-center justify-center rounded-[var(--bethere-radius-md)] border-2 border-[var(--bethere-primary)] bg-transparent px-6 font-bold text-[var(--bethere-primary)]"
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={teamBusy}
            className="inline-flex min-h-11 items-center justify-center rounded-[var(--bethere-radius-md)] bg-[var(--bethere-primary)] px-6 font-bold text-white disabled:opacity-60"
            onClick={handleDeleteMember}
          >
            Remove
          </button>
        </div>
      </Portal>
    </div>
  );
}
